import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";

const TONES = {
  "md-addiction": "#0b0a09",
  "dewan-royal-oud": "#2a1013",
  "atw-signature": "#2b2416",
  "atw-voyage": "#0b1a2e",
};

export default function PerfumeAtelier({ perfumes }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { add, setOpen } = useCart();
  const { format } = useCurrency();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const idx = Math.min(perfumes.length - 1, Math.max(0, Math.floor(v * perfumes.length)));
    if (idx !== active) setActive(idx);
  });

  if (!perfumes.length) return <section data-testid="perfume-atelier" className="h-[220vh] bg-obsidian" />;
  const p = perfumes[Math.min(active, perfumes.length - 1)];

  const handleAdd = () => {
    const v = p.variants?.[0];
    add(p, v?.label || null, v?.price ?? p.price);
    setOpen(true);
    toast.success(`${p.name} added to your cart`, { action: { label: "View", onClick: () => setOpen(true) } });
  };

  return (
    <section ref={ref} data-testid="perfume-atelier" style={{ height: `${perfumes.length * 55}vh` }} className="relative bg-obsidian">
      <motion.div
        animate={{ backgroundColor: TONES[p.id] || "#0b0a09" }}
        transition={{ duration: 0.9 }}
        className="sticky top-0 h-screen overflow-hidden text-ivory"
      >
        <img src="/brand/atelier-model.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-[80%_center] opacity-25 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_50%,rgba(212,175,55,0.22),transparent_60%)]" />
        <img src="/brand/logo-seal.png" alt="" aria-hidden="true" className="absolute -right-24 -bottom-24 w-[34rem] opacity-[0.06] pointer-events-none" />

        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 items-center gap-10">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="text-[10px] tracking-[0.5em] uppercase text-gold-light">The Perfume Atelier · by Dewan Ali · {String(active + 1).padStart(2, "0")} / {String(perfumes.length).padStart(2, "0")}</p>
            <AnimatePresence mode="wait">
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30, rotateX: 20 }}
                animate={{ opacity: 1, y: 0, rotateX: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformPerspective: 900 }}
              >
                <h2 data-testid="atelier-name" className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.9] mt-5">
                  {p.name.split(" ").slice(0, -1).join(" ")}{" "}
                  <span className="italic text-gold-light">{p.name.split(" ").slice(-1)}</span>
                </h2>
                <p className="mt-6 text-ivory/65 leading-relaxed max-w-md">{p.description}</p>
                {p.notes && (
                  <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-xs">
                    {["top", "heart", "base"].map((k) => (
                      <div key={k}>
                        <p className="text-[9px] tracking-[0.3em] uppercase text-gold-light/80">{k}</p>
                        <p className="text-ivory/70 mt-1">{p.notes[k]}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <button
                    data-testid={`atelier-add-${p.id}`}
                    onClick={handleAdd}
                    className="gold-button px-7 py-4 text-[11px] tracking-[0.25em] uppercase font-medium"
                  >
                    <span className="relative z-10">Add · {format(p.variants?.[0]?.price ?? p.price)}</span>
                  </button>
                  <Link
                    to={`/product/${p.id}`}
                    data-testid={`atelier-discover-${p.id}`}
                    className="group flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ivory/70 hover:text-gold-light transition-colors"
                  >
                    Discover
                    <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-7 order-1 lg:order-2 relative h-[46vh] lg:h-[78vh]" style={{ perspective: 1400 }}>
            <AnimatePresence mode="popLayout">
              <motion.img
                key={p.id}
                src={p.image}
                alt={p.name}
                initial={{ opacity: 0, rotateY: 35, x: 120, scale: 0.9 }}
                animate={{ opacity: 1, rotateY: -6, x: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: -35, x: -120, scale: 0.9 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover shadow-[0_60px_120px_-40px_rgba(0,0,0,0.8)] border border-gold/20"
              />
            </AnimatePresence>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
              className="absolute -left-6 bottom-8 hidden lg:block bg-ivory text-ink px-5 py-4 shadow-2xl"
            >
              <p className="text-[9px] tracking-[0.3em] uppercase text-ink/45">{p.tag}</p>
              <p className="font-serif text-xl mt-1">{p.variants?.map((v) => v.label).join(" · ")}</p>
            </motion.div>
          </div>
        </div>

        <div className="absolute left-6 lg:left-12 bottom-8 flex gap-3">
          {perfumes.map((x, i) => (
            <span key={x.id} className={`h-px transition-[width,background-color] duration-500 ${i === active ? "w-12 bg-gold-light" : "w-5 bg-ivory/30"}`} />
          ))}
        </div>
      </motion.div>
    </section>
  );
}
